import React, { useState, useEffect, useCallback } from 'react';
import {
  Search,
  ClipboardList,
  ChevronLeft,
  ChevronRight,
  Clock,
  User,
} from 'lucide-react';
import { Card, CardContent } from '@/components/common/Card';
import { Badge } from '@/components/common/Badge';
import { Button } from '@/components/common/Button';
import { SlideOverPanel } from '@/components/common/SlideOverPanel';
import { Spinner } from '@/components/common/Spinner';
import { cn } from '@/utils/cn';
import toast from 'react-hot-toast';
import { submissionService, type Submission } from '@/services/submission.service';

const PAGE_SIZE = 25;

const STATUS_FILTERS = ['all', 'pending', 'confirmed', 'processing', 'completed', 'cancelled', 'scheduled'] as const;
type StatusFilter = (typeof STATUS_FILTERS)[number];

const statusColors: Record<string, string> = {
  pending: 'bg-amber-100 text-amber-700',
  confirmed: 'bg-blue-100 text-blue-700',
  processing: 'bg-violet-100 text-violet-700',
  completed: 'bg-emerald-100 text-emerald-700',
  cancelled: 'bg-red-100 text-red-700',
  scheduled: 'bg-indigo-100 text-indigo-800',
};

const workflowColors: Record<string, string> = {
  order: 'bg-blue-100 text-blue-700',
  booking: 'bg-violet-100 text-violet-700',
  lead: 'bg-emerald-100 text-emerald-700',
  support: 'bg-amber-100 text-amber-700',
  inquiry: 'bg-pink-100 text-pink-700',
};

const statusTransitions: Record<string, string[]> = {
  pending: ['confirmed', 'cancelled'],
  confirmed: ['processing', 'cancelled'],
  processing: ['completed', 'cancelled'],
  completed: [],
  cancelled: [],
  scheduled: ['pending', 'cancelled'],
};

function getRelativeTime(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diff = now - then;
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (seconds < 60) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function getLeadScoreColor(score: number): string {
  if (score >= 8) return 'text-emerald-600';
  if (score >= 5) return 'text-amber-600';
  return 'text-red-600';
}

function getCustomerName(submission: Submission): string {
  if (submission.customer_snapshot) {
    const snap = submission.customer_snapshot;
    if (snap.name) return String(snap.name);
  }
  const data = submission.collected_data;
  if (data.name) return String(data.name);
  return '-';
}

function getCustomerPhone(submission: Submission): string | null {
  if (submission.customer_snapshot) {
    const snap = submission.customer_snapshot;
    if (snap.phone) return String(snap.phone);
  }
  const data = submission.collected_data;
  if (data.phone) return String(data.phone);
  return null;
}

function getWorkflowColor(name: string): string {
  const lower = name.toLowerCase();
  for (const [key, color] of Object.entries(workflowColors)) {
    if (lower.includes(key)) return color;
  }
  return 'bg-gray-100 text-gray-700';
}

export const SubmissionsPage: React.FC = () => {
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');

  // Detail panel
  const [selectedSubmission, setSelectedSubmission] = useState<Submission | null>(null);
  const [statusUpdating, setStatusUpdating] = useState(false);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setSearchDebounced(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Fetch submissions
  const fetchSubmissions = useCallback(async () => {
    setLoading(true);
    try {
      const result = await submissionService.getSubmissions({
        status: statusFilter !== 'all' ? statusFilter : undefined,
        search: searchDebounced || undefined,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      });
      setSubmissions(result.data);
      setTotal(result.meta.totalItems);
    } catch {
      toast.error('Failed to load submissions');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, searchDebounced, page]);

  useEffect(() => {
    fetchSubmissions();
  }, [fetchSubmissions]);

  // Reset page when filters change
  useEffect(() => {
    setPage(0);
  }, [searchDebounced, statusFilter]);

  const handleStatusChange = async (submissionId: string, newStatus: string) => {
    setStatusUpdating(true);
    try {
      const updated = await submissionService.updateStatus(submissionId, newStatus);
      setSelectedSubmission(updated);
      fetchSubmissions();
      toast.success(`Status updated to ${newStatus}`);
    } catch {
      toast.error('Failed to update status');
    } finally {
      setStatusUpdating(false);
    }
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3">
          <h1 className="text-3xl font-bold">Orders</h1>
          {total > 0 && (
            <span className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded-full">{total}</span>
          )}
        </div>
        <p className="text-muted-foreground mt-1">Orders and requests from your customers</p>
      </div>

      {/* Status filter pills */}
      <div className="flex flex-wrap items-center gap-2">
        {STATUS_FILTERS.map((status) => (
          <button
            key={status}
            onClick={() => setStatusFilter(status)}
            className={cn(
              'px-3 py-1.5 text-sm font-medium rounded-xl border-2 transition-colors capitalize',
              statusFilter === status
                ? 'border-[#1a1a2e] bg-[#1a1a2e] text-white shadow-[3px_3px_0px_#1a1a2e]'
                : 'border-[#1a1a2e] bg-white text-gray-600 hover:bg-gray-50'
            )}
          >
            {status}
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <input
          type="text"
          placeholder="Search by workflow name or data..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-2 text-sm border-2 border-[#1a1a2e] rounded-xl bg-background focus:outline-none focus:ring-2 focus:ring-[#1a1a2e]/20 focus:border-[#1a1a2e] focus:shadow-[3px_3px_0px_#1a1a2e]"
        />
      </div>

      {/* Table */}
      <Card className="!transform-none !shadow-[3px_3px_0px_#1a1a2e] hover:!shadow-[3px_3px_0px_#1a1a2e]">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Spinner size="lg" />
            </div>
          ) : submissions.length === 0 ? (
            <div className="text-center py-16">
              <ClipboardList className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground font-medium">
                {searchDebounced || statusFilter !== 'all'
                  ? 'No orders match your filters'
                  : 'No orders yet'}
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                Orders appear when customers complete actions through your agents
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b-2 border-[#1a1a2e]/10 bg-gray-50/50">
                    <th className="text-left font-medium text-muted-foreground px-4 py-3 w-12">#</th>
                    <th className="text-left font-medium text-muted-foreground px-4 py-3">Workflow</th>
                    <th className="text-left font-medium text-muted-foreground px-4 py-3">Customer</th>
                    <th className="text-right font-medium text-muted-foreground px-4 py-3">Total</th>
                    <th className="text-left font-medium text-muted-foreground px-4 py-3">Status</th>
                    <th className="text-left font-medium text-muted-foreground px-4 py-3">Created</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {submissions.map((submission, idx) => {
                    const customerName = getCustomerName(submission);
                    const customerPhone = getCustomerPhone(submission);
                    return (
                      <tr
                        key={submission.id}
                        onClick={() => setSelectedSubmission(submission)}
                        className="hover:bg-gray-50 cursor-pointer transition-colors"
                      >
                        <td className="px-4 py-3 text-muted-foreground text-xs tabular-nums">
                          {page * PAGE_SIZE + idx + 1}
                        </td>
                        <td className="px-4 py-3">
                          <Badge
                            variant="outline"
                            className={cn('text-xs', getWorkflowColor(submission.workflow_name))}
                          >
                            {submission.workflow_name}
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-start gap-2">
                            <User className="h-3.5 w-3.5 text-muted-foreground mt-0.5 shrink-0" />
                            <div className="min-w-0">
                              <p className="text-gray-900 truncate">{customerName}</p>
                              {customerPhone && (
                                <p className="text-xs text-muted-foreground truncate">{customerPhone}</p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums">
                          {(() => {
                            const items = submission.collected_data.items;
                            if (Array.isArray(items) && items.length > 0) {
                              const total = (items as any[]).reduce((sum: number, item: any) => {
                                const price = parseFloat(String(item.price || 0));
                                const qty = Number(item.quantity || 1);
                                return sum + (price * qty);
                              }, 0);
                              return total > 0 ? <span className="font-medium text-gray-900">${total.toFixed(2)}</span> : <span className="text-gray-400">&mdash;</span>;
                            }
                            return <span className="text-gray-400">&mdash;</span>;
                          })()}
                        </td>
                        <td className="px-4 py-3">
                          <Badge
                            variant="outline"
                            className={cn('text-xs capitalize', statusColors[submission.status] || 'bg-gray-100 text-gray-700')}
                          >
                            {submission.status}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-muted-foreground text-xs whitespace-nowrap">
                          <div className="flex items-center gap-1">
                            <Clock className="h-3.5 w-3.5" />
                            {getRelativeTime(submission.created_at)}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t">
              <span className="text-sm text-muted-foreground">
                {total} order{total !== 1 ? 's' : ''}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === 0}
                  onClick={() => setPage((p) => p - 1)}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm text-muted-foreground">
                  {page + 1} / {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages - 1}
                  onClick={() => setPage((p) => p + 1)}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Panel */}
      <SlideOverPanel
        isOpen={!!selectedSubmission}
        onClose={() => setSelectedSubmission(null)}
        title={selectedSubmission?.workflow_name || 'Order Details'}
        subtitle={selectedSubmission ? `ID: ${selectedSubmission.id.slice(0, 8)}...` : undefined}
        size="lg"
      >
        {selectedSubmission ? (
          <div className="space-y-6">
            {/* Status */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">Status</h3>
              <div className="flex flex-wrap items-center gap-2">
                <Badge
                  variant="outline"
                  className={cn('text-sm capitalize', statusColors[selectedSubmission.status] || 'bg-gray-100 text-gray-700')}
                >
                  {selectedSubmission.status}
                </Badge>
                {statusTransitions[selectedSubmission.status]?.map((nextStatus) => (
                  <Button
                    key={nextStatus}
                    size="sm"
                    variant="outline"
                    disabled={statusUpdating}
                    onClick={() => handleStatusChange(selectedSubmission.id, nextStatus)}
                    className="capitalize"
                  >
                    {nextStatus}
                  </Button>
                ))}
              </div>
            </div>

            {/* Items (when itemized) */}
            {Array.isArray(selectedSubmission.collected_data.items) && (selectedSubmission.collected_data.items as any[]).length > 0 && (
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">Items</h3>
                <div className="rounded-xl border-2 border-[#1a1a2e]/10 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50/80">
                        <th className="text-left font-medium text-gray-500 px-3 py-2">Item</th>
                        <th className="text-center font-medium text-gray-500 px-3 py-2 w-16">Qty</th>
                        <th className="text-right font-medium text-gray-500 px-3 py-2 w-20">Price</th>
                        <th className="text-right font-medium text-gray-500 px-3 py-2 w-20">Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {(selectedSubmission.collected_data.items as any[]).map((item: any, i: number) => {
                        const price = parseFloat(String(item.price || 0));
                        const qty = Number(item.quantity || 1);
                        const lineTotal = price * qty;
                        return (
                          <tr key={i}>
                            <td className="px-3 py-2 text-gray-900">{item.name || `Item ${i + 1}`}</td>
                            <td className="px-3 py-2 text-center text-gray-600">{qty}</td>
                            <td className="px-3 py-2 text-right text-gray-600 tabular-nums">{price > 0 ? `$${price.toFixed(2)}` : '\u2014'}</td>
                            <td className="px-3 py-2 text-right font-medium text-gray-900 tabular-nums">{lineTotal > 0 ? `$${lineTotal.toFixed(2)}` : '\u2014'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr className="bg-gray-50/80 border-t-2 border-gray-200">
                        <td colSpan={3} className="px-3 py-2 text-right font-semibold text-gray-700">Total</td>
                        <td className="px-3 py-2 text-right font-bold text-gray-900 tabular-nums">
                          ${(selectedSubmission.collected_data.items as any[]).reduce((sum: number, item: any) => {
                            return sum + (parseFloat(String(item.price || 0)) * Number(item.quantity || 1));
                          }, 0).toFixed(2)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )}

            {/* Customer Snapshot */}
            {selectedSubmission.customer_snapshot && (() => {
              const entries = Object.entries(selectedSubmission.customer_snapshot).filter(([, value]) => value != null && String(value).trim() !== '');
              if (entries.length === 0) return null;
              return (
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">Customer</h3>
                  <div className="rounded-xl border-2 border-[#1a1a2e]/10 p-3 space-y-2">
                    {entries.map(([key, value]) => (
                      <div key={key} className="flex justify-between text-sm">
                        <span className="text-muted-foreground capitalize">{key.replace(/_/g, ' ')}</span>
                        <span className="font-medium text-gray-900">{String(value)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* Collected Fields */}
            {(() => {
              const skipKeys = new Set(['items', '_submission_id', '_submission']);
              const fields = Object.entries(selectedSubmission.collected_data).filter(([key]) => !skipKeys.has(key));
              if (fields.length === 0) return null;
              return (
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">Details</h3>
                  <div className="rounded-xl border-2 border-[#1a1a2e]/10 p-3 space-y-2">
                    {fields.map(([key, value]) => (
                      <div key={key} className="flex justify-between text-sm gap-4">
                        <span className="text-muted-foreground capitalize shrink-0">{key.replace(/_/g, ' ')}</span>
                        <span className="font-medium text-gray-900 text-right break-words min-w-0">
                          {typeof value === 'object' && value !== null ? JSON.stringify(value, null, 2) : String(value || '\u2014')}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* Pushed to stores */}
            {Array.isArray((selectedSubmission.metadata as any)?.platform_orders) && ((selectedSubmission.metadata as any).platform_orders as any[]).length > 0 && (
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">Pushed to</h3>
                <div className="space-y-2">
                  {((selectedSubmission.metadata as any).platform_orders as any[]).map((po: any, i: number) => (
                    <div key={i} className="flex items-center justify-between rounded-xl border-2 border-emerald-200 bg-emerald-50 p-3 text-sm">
                      <div>
                        <p className="font-medium text-emerald-800">{po.integration_name}</p>
                        <p className="text-xs text-emerald-600 capitalize">{(po.platform_type || '').replace('_', ' ')} · {po.items_count} items</p>
                      </div>
                      {po.order_id && <span className="font-mono text-xs text-emerald-700">#{po.order_id}</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Info */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">Info</h3>
              <div className="rounded-xl border-2 border-[#1a1a2e]/10 p-3 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Type</span>
                  <Badge variant="outline" className={cn('text-xs', getWorkflowColor(selectedSubmission.workflow_name))}>
                    {selectedSubmission.workflow_name}
                  </Badge>
                </div>
                {selectedSubmission.external_id && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Store Order</span>
                    <span className="font-mono text-xs text-gray-900">#{selectedSubmission.external_id}</span>
                  </div>
                )}
                {selectedSubmission.lead_score != null && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Lead Score</span>
                    <span className={cn('font-medium', getLeadScoreColor(selectedSubmission.lead_score))}>
                      {selectedSubmission.lead_score}/10
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Timestamps */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">Timestamps</h3>
              <div className="rounded-xl border-2 border-[#1a1a2e]/10 p-3 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Created</span>
                  <span className="text-gray-900">{new Date(selectedSubmission.created_at).toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Updated</span>
                  <span className="text-gray-900">{new Date(selectedSubmission.updated_at).toLocaleString()}</span>
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </SlideOverPanel>
    </div>
  );
};
